import { Plus, Trash2 } from "lucide-react";
import React from "react";
import type { Path } from "react-hook-form";
import { useFieldArray, UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import RichTextEditor from "@/components/rich-text-editor";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { RequiredLabel } from "@/components/ui/required-label";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/i18n";
import { useInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.hooks";

import { TProductFormData } from "../../products.schema";
import { EProductType } from "../../products.type";
import type { TAutoFormData, TPlan } from "../../schemas";
import { filterVehiclePlanAdjustmentsForForm } from "../../schemas";
import { SoulhubExternalProductIdField } from "../soulhub-external-product-id-field";
import { PlanAdjustmentsSection } from "./plan-adjustments-section";
/** Vigência: planilha + opção legada vitalícia. */
const AUTO_VIGENCY_VALUES = [
  "pluriannual",
  "annual",
  "monthly",
  "daily",
  "pay_per_use",
  "lifetime",
] as const;

type AutoPath = Path<TAutoFormData>;

/** API/edit payload may carry `id` and legacy `capital_ranges` alongside auto fields. */
type AutoFormRuntimeValues = TAutoFormData & {
  id?: string;
  capital_ranges?: IAutoCapitalRangeFromApi[];
};

interface IAutoCapitalRangeFromApi {
  plan_name?: string | null;
  description?: string | null;
  new_coverages?: string | null;
  plan_adjustments?: unknown;
  commission_auto?: number | string | null;
  net_premium?: number | null;
  markup?: number | null;
  premium_value?: number | null;
}

function getAutoRuntimeValues(
  getValues: UseFormReturn<TProductFormData>["getValues"],
): AutoFormRuntimeValues {
  return getValues() as unknown as AutoFormRuntimeValues;
}

export interface IAutoFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TProductFormData>;
  onSubmit: (values: TProductFormData) => void;
  isEditMode?: boolean;
}

export const InsuranceAutoForm: React.FC<IAutoFormProps> = ({
  open,
  form,
  onSubmit,
  isEditMode,
}) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = React.useState("data");
  const vehicleEmission = form.watch("vehicle_emission_type" as AutoPath);
  const isCollective = form.watch("is_collective_policy" as AutoPath);
  const hasKmLimit = form.watch("has_km_limit" as AutoPath);
  const hasYearLimit = form.watch("has_manufacturing_year_limit" as AutoPath);
  const hasMultiplePlans = form.watch("has_multiple_plans" as AutoPath);
  const premiumMode = form.watch("premium_mode" as AutoPath);
  
  // Gerenciamento de planos (internamente usa plans, mas sincroniza com capital_ranges)
  const {
    fields: plansFields,
    append: appendPlan,
    remove: removePlan,
  } = useFieldArray({ control: form.control, name: "plans" });

  // Validação por etapa
  const validateTab = React.useCallback(
    async (tab: string): Promise<boolean> => {
      const fieldsByTab: Record<string, AutoPath[]> = {
        data: [
          "name",
          "insurance_company_id",
          "type_of_vigency",
          "vehicle_emission_type",
          "has_km_limit",
          "has_manufacturing_year_limit",
          "susep_product_code",
          "premium_mode",
          "has_multiple_plans",
          "vehicle_product_config",
          "is_collective_policy",
          "has_estipulante",
          "has_sub_estipulante",
          "policy_number",
          "policy_start_date",
          "policy_end_date",
          "insurance_policy_id",
          "cod_sub",
          "prefixed_premium_value",
          "max_vehicle_age",
          "km_max_permitted",
          "min_manufacturing_year",
        ],
        price: [],
      };

      const plansOn =
        form.getValues("has_multiple_plans" as AutoPath) === true;
      const fieldsByTabResolved: Record<string, AutoPath[]> = {
        ...fieldsByTab,
        price: plansOn ? ["plans"] : [],
      };
      const fieldsToValidate = fieldsByTabResolved[tab] || [];
      if (fieldsToValidate.length === 0) return true;

      const result = await form.trigger(fieldsToValidate);
      return result;
    },
    [form]
  );

  const handleTabChange = React.useCallback(
    async (value: string) => {
      const tabOrder = ["data", "price"];
      const currentIndex = tabOrder.indexOf(activeTab);
      const newIndex = tabOrder.indexOf(value);

      // Permite voltar para tabs anteriores sem validar
      if (newIndex < currentIndex) {
        setActiveTab(value);
        return;
      }

      // Valida antes de avançar
      const isValid = await validateTab(activeTab);
      if (isValid) {
        setActiveTab(value);
      } else {
        // Mostra mensagem de erro
        form.trigger(
          activeTab === "data"
            ? ([
                "name",
                "insurance_company_id",
                "type_of_vigency",
                "vehicle_emission_type",
                "has_km_limit",
                "has_manufacturing_year_limit",
                "susep_product_code",
                "premium_mode",
                "has_multiple_plans",
                "vehicle_product_config",
              ] satisfies AutoPath[])
            : form.getValues("has_multiple_plans" as AutoPath) === true
              ? (["plans"] satisfies AutoPath[])
              : ([] satisfies AutoPath[]),
        );
      }
    },
    [activeTab, validateTab, form, hasMultiplePlans]
  );

  // Função para adicionar um novo plano
  const addPlan = () => {
    appendPlan({
      name: "",
      description: "",
      new_coverages: "",
      plan_adjustments: [],
    });
  };

  // Função para remover um plano
  const removePlanHandler = (planIndex: number) => {
    removePlan(planIndex);
  };

  const { insuranceCompanies } = useInsuranceCompanies();

  // Interceptar submit para converter plans → capital_ranges
  const handleFormSubmit = (data: TProductFormData) => {
    // SOLUÇÃO DEFINITIVA: Ler os valores diretamente do form state em tempo real
    const formValues = getAutoRuntimeValues(form.getValues);
    const plansFromForm = formValues.plans ?? [];

    // Se o form tem plans, usar eles. Senão, usar plansFields como fallback
    const effectivePlans =
      plansFromForm.length > 0
        ? plansFromForm
        : plansFields.length > 0
          ? plansFields
          : [];

    // Sempre tentar converter de plans primeiro (usuário pode ter editado)
    if (effectivePlans.length > 0) {
        const capital_ranges = effectivePlans.map((planRaw) => {
        const plan = planRaw as TPlan;
        return {
          capital_value: 0,
          premium_value: 0,
          plan_name: plan.name || "",
          description: plan.description || "",
          new_coverages: plan.new_coverages || "",
          plan_adjustments: filterVehiclePlanAdjustmentsForForm(
            plan.plan_adjustments || [],
          ),
          markup_mode: "percentage" as const,
        };
      });

      const merged: Record<string, unknown> = {
        ...(data as unknown as Record<string, unknown>),
        capital_ranges,
      };
      delete merged["plans"];
      onSubmit(merged as TProductFormData);
      return;
    }

    // Fallback: Se já tem capital_ranges preenchido mas não tem plans
    const dataWithRanges = data as unknown as AutoFormRuntimeValues;
    const capitalRangesFallback = dataWithRanges.capital_ranges;
    if (capitalRangesFallback && capitalRangesFallback.length > 0) {
      const merged: Record<string, unknown> = {
        ...(dataWithRanges as unknown as Record<string, unknown>),
      };
      delete merged["plans"];
      onSubmit(merged as TProductFormData);
      return;
    }
    
    // Se não tem nem plans nem capital_ranges, enviar vazio
    onSubmit(data);
  };

  // Carregar capital_ranges → plans quando o formulário abrir
  const plansInitialized = React.useRef(false);
  const lastProductId = React.useRef<string | null>(null);
  const wasOpen = React.useRef(false);
  
  React.useEffect(() => {
    // Resetar flag quando o dialog abre (mudança de false para true)
    if (open && !wasOpen.current) {
      plansInitialized.current = false;
      lastProductId.current = null;
    }
    wasOpen.current = open;
  }, [open]);
  
  React.useEffect(() => {
    // Detectar mudança de produto para forçar reinicialização
    const snap = getAutoRuntimeValues(form.getValues);
    const currentProductId = snap.id;
    const currentPlans = snap.plans ?? [];
    const capitalRanges = snap.capital_ranges ?? [];
    
    // Forçar reinicialização se:
    // 1. Mudou o ID do produto OU
    // 2. Tem capital_ranges mas plans está vazio (significa que foi resetado mas não foi convertido)
    if (currentProductId && currentProductId !== lastProductId.current) {
      plansInitialized.current = false;
      lastProductId.current = currentProductId;
    } else if (capitalRanges.length > 0 && currentPlans.length === 0 && plansInitialized.current) {
      plansInitialized.current = false;
    }

    if (open && !plansInitialized.current) {
      setActiveTab("data");
      
      // Aguardar um momento para garantir que o form foi populado
      setTimeout(() => {
        const rangesFromDb =
          getAutoRuntimeValues(form.getValues).capital_ranges ?? [];

        // Só inicializar se tiver capital_ranges E ainda não inicializou
        if (rangesFromDb.length > 0 && !plansInitialized.current) {
          // Limpar plans existentes
          const currentPlansLength = plansFields.length;
          for (let i = currentPlansLength - 1; i >= 0; i--) {
            removePlan(i);
          }

          // Converter capital_ranges para plans
          const convertedPlans = rangesFromDb.map((range: IAutoCapitalRangeFromApi) => {
            const plan: TPlan = {
              name: range.plan_name || "",
              description: range.description || "",
              new_coverages: range.new_coverages || "",
              plan_adjustments: filterVehiclePlanAdjustmentsForForm(
                Array.isArray(range.plan_adjustments)
                  ? (range.plan_adjustments as TPlan["plan_adjustments"])
                  : [],
              ),
            };

            return plan;
          });

          // Adicionar os plans convertidos
          convertedPlans.forEach((plan: TPlan, index: number) => {
            appendPlan(plan);

            // CRÍTICO: Registrar cada campo individualmente no formulário
            // Isso garante que o React Hook Form reconheça os valores
            setTimeout(() => {
              form.setValue(`plans.${index}.name` as AutoPath, plan.name);
              form.setValue(
                `plans.${index}.description` as AutoPath,
                plan.description,
              );
              form.setValue(
                `plans.${index}.new_coverages` as AutoPath,
                plan.new_coverages ?? "",
              );
              form.setValue(
                `plans.${index}.plan_adjustments` as AutoPath,
                plan.plan_adjustments ?? [],
              );
            }, 50);
          });
          
          plansInitialized.current = true;
        }
      }, 100);
    } else if (!open) {
      // Resetar quando fechar
      plansInitialized.current = false;
      lastProductId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit appendPlan/removePlan/plansFields.length to avoid plan bootstrap loops
  }, [open, form]);

  if (open) {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="data">
                1. {t("products.title.tab.data")}
              </TabsTrigger>
              <TabsTrigger
                value="price"
                disabled={hasMultiplePlans === false}
              >
                2. {t("products.title.tab.plans")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="data">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="name" required>
                          {t("products.form.fields.name")}
                        </RequiredLabel>
                        <FormControl>
                          <Input {...field} id="name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <SoulhubExternalProductIdField form={form} />
                  <FormField
                    control={form.control}
                    name="insurance_company_id"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="insurance_company_id" required>
                          {t("products.auto.fields.insurance_company_id")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-[100%]">
                              <SelectValue
                                placeholder={t(
                                  "products.form.fields.insurance_company_id.placeholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {insuranceCompanies.map((company) => (
                                  <SelectItem
                                    key={company.id}
                                    value={company.id || ""}
                                  >
                                    {company.name} - {company.cnpj}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 border rounded-md p-4">
                    <FormField
                      control={form.control}
                      name={"vehicle_emission_type" as AutoPath}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.vehicle.fields.emission_type")}
                          </RequiredLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={(field.value as string) || ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t("generic.selectAnOption")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="apolice">
                                {t("products.vehicle.emission.apolice")}
                              </SelectItem>
                              <SelectItem value="bilhete">
                                {t("products.vehicle.emission.bilhete")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {vehicleEmission === "apolice" && (
                      <FormField
                        control={form.control}
                        name={"is_collective_policy" as AutoPath}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <FormLabel>
                                {t("products.vehicle.fields.collective_policy")}
                              </FormLabel>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value === true}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <FormField
                      control={form.control}
                      name={"susep_product_code" as AutoPath}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.vehicle.fields.susep_product_code")}
                          </RequiredLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={(field.value as string) || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={"has_km_limit" as AutoPath}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <FormLabel>
                            {t("products.vehicle.fields.has_km_limit")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={"has_manufacturing_year_limit" as AutoPath}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <FormLabel>
                            {t("products.vehicle.fields.has_year_limit")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={"premium_mode" as AutoPath}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.vehicle.fields.premium_mode")}
                          </RequiredLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={(field.value as string) || ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t("generic.selectAnOption")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="prefixed">
                                {t("products.vehicle.premium.prefixed")}
                              </SelectItem>
                              <SelectItem value="calculated">
                                {t("products.vehicle.premium.calculated")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={"has_multiple_plans" as AutoPath}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2">
                          <FormLabel>
                            {t("products.vehicle.fields.has_multiple_plans")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {premiumMode === "prefixed" && (
                      <FormField
                        control={form.control}
                        name={"prefixed_premium_value" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel required>
                              {t("products.vehicle.fields.prefixed_premium")}
                            </RequiredLabel>
                            <FormControl>
                              <RealCurrencyInput
                                value={Number(field.value) || 0}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <div className="col-span-full border-t pt-4">
                      <Card className="p-4">
                        <h3 className="text-sm font-semibold mb-3">
                          {t("products.vehicle.commercial.boxTitle")}
                        </h3>
                        <div className="space-y-4">
                          <div className="rounded-lg border border-border p-3 space-y-3">
                            <FormField
                              control={form.control}
                              name={
                                "vehicle_product_config.commercial.commission_enabled" as AutoPath
                              }
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center gap-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value === true}
                                      onCheckedChange={(c) =>
                                        field.onChange(c === true)
                                      }
                                    />
                                  </FormControl>
                                  <FormLabel className="!mt-0 font-medium cursor-pointer">
                                    {t("products.vehicle.commercial.commission")}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                            {form.watch(
                              "vehicle_product_config.commercial.commission_enabled" as AutoPath,
                            ) === true && (
                              <>
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.commission_mode" as AutoPath
                                  }
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        {t(
                                          "products.vehicle.commercial.valueMode",
                                        )}
                                      </FormLabel>
                                      <Select
                                        onValueChange={field.onChange}
                                        value={(field.value as string) || "percentage"}
                                      >
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="percentage">
                                            {t(
                                              "products.vehicle.commercial.modePercentage",
                                            )}
                                          </SelectItem>
                                          <SelectItem value="real_currency">
                                            {t(
                                              "products.vehicle.commercial.modeCurrency",
                                            )}
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.commission_value" as AutoPath
                                  }
                                  render={({ field }) =>
                                    form.watch(
                                      "vehicle_product_config.commercial.commission_mode" as AutoPath,
                                    ) === "real_currency" ? (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.commission_value_currency",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <RealCurrencyInput
                                            value={Number(field.value) || 0}
                                            onChange={field.onChange}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    ) : (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.commission_percent",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <div className="relative">
                                            <Input
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              className="pr-8"
                                              value={
                                                field.value === undefined ||
                                                field.value === null
                                                  ? ""
                                                  : String(field.value)
                                              }
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                field.onChange(
                                                  raw === ""
                                                    ? undefined
                                                    : Number(raw),
                                                );
                                              }}
                                            />
                                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                              %
                                            </span>
                                          </div>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )
                                  }
                                />
                              </>
                            )}
                          </div>

                          <div className="rounded-lg border border-border p-3 space-y-3">
                            <FormField
                              control={form.control}
                              name={
                                "vehicle_product_config.commercial.pro_labore_enabled" as AutoPath
                              }
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center gap-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value === true}
                                      onCheckedChange={(c) =>
                                        field.onChange(c === true)
                                      }
                                    />
                                  </FormControl>
                                  <FormLabel className="!mt-0 font-medium cursor-pointer">
                                    {t("products.vehicle.commercial.pro_labore")}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                            {form.watch(
                              "vehicle_product_config.commercial.pro_labore_enabled" as AutoPath,
                            ) === true && (
                              <>
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.pro_labore_mode" as AutoPath
                                  }
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        {t(
                                          "products.vehicle.commercial.valueMode",
                                        )}
                                      </FormLabel>
                                      <Select
                                        onValueChange={field.onChange}
                                        value={
                                          (field.value as string) || "real_currency"
                                        }
                                      >
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="percentage">
                                            {t(
                                              "products.vehicle.commercial.modePercentage",
                                            )}
                                          </SelectItem>
                                          <SelectItem value="real_currency">
                                            {t(
                                              "products.vehicle.commercial.modeCurrency",
                                            )}
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.pro_labore_value" as AutoPath
                                  }
                                  render={({ field }) =>
                                    form.watch(
                                      "vehicle_product_config.commercial.pro_labore_mode" as AutoPath,
                                    ) === "percentage" ? (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.pro_labore_percent",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <div className="relative">
                                            <Input
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              className="pr-8"
                                              value={
                                                field.value === undefined ||
                                                field.value === null
                                                  ? ""
                                                  : String(field.value)
                                              }
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                field.onChange(
                                                  raw === ""
                                                    ? undefined
                                                    : Number(raw),
                                                );
                                              }}
                                            />
                                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                              %
                                            </span>
                                          </div>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    ) : (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.pro_labore_value_currency",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <RealCurrencyInput
                                            value={Number(field.value) || 0}
                                            onChange={field.onChange}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )
                                  }
                                />
                              </>
                            )}
                          </div>

                          <div className="rounded-lg border border-border p-3 space-y-3">
                            <FormField
                              control={form.control}
                              name={
                                "vehicle_product_config.commercial.markup_enabled" as AutoPath
                              }
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center gap-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value === true}
                                      onCheckedChange={(c) =>
                                        field.onChange(c === true)
                                      }
                                    />
                                  </FormControl>
                                  <FormLabel className="!mt-0 font-medium cursor-pointer">
                                    {t("products.vehicle.commercial.markup")}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                            {form.watch(
                              "vehicle_product_config.commercial.markup_enabled" as AutoPath,
                            ) === true && (
                              <>
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.markup_mode" as AutoPath
                                  }
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        {t(
                                          "products.vehicle.commercial.valueMode",
                                        )}
                                      </FormLabel>
                                      <Select
                                        onValueChange={field.onChange}
                                        value={(field.value as string) || "percentage"}
                                      >
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="percentage">
                                            {t(
                                              "products.vehicle.commercial.markupModePercentOnNet",
                                            )}
                                          </SelectItem>
                                          <SelectItem value="real_currency">
                                            {t(
                                              "products.vehicle.commercial.markupModeFixedValue",
                                            )}
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={
                                    "vehicle_product_config.commercial.markup_value" as AutoPath
                                  }
                                  render={({ field }) =>
                                    form.watch(
                                      "vehicle_product_config.commercial.markup_mode" as AutoPath,
                                    ) === "real_currency" ? (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.markup_value_currency",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <RealCurrencyInput
                                            value={Number(field.value) || 0}
                                            onChange={field.onChange}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    ) : (
                                      <FormItem>
                                        <FormLabel>
                                          {t(
                                            "products.vehicle.commercial.markup_percent",
                                          )}
                                        </FormLabel>
                                        <FormControl>
                                          <div className="relative">
                                            <Input
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              className="pr-8"
                                              value={
                                                field.value === undefined ||
                                                field.value === null
                                                  ? ""
                                                  : String(field.value)
                                              }
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                field.onChange(
                                                  raw === ""
                                                    ? undefined
                                                    : Number(raw),
                                                );
                                              }}
                                            />
                                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                              %
                                            </span>
                                          </div>
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )
                                  }
                                />
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  {vehicleEmission === "apolice" && isCollective === true && (
                    <React.Fragment>
                      <FormField
                        control={form.control}
                        name={"policy_number" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel required htmlFor="policy_number">
                              {t("products.auto.fields.policy_number")}
                            </RequiredLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="policy_number"
                                value={(field.value as string) || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={"policy_start_date" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel required>
                              {t("products.auto.fields.policy_start_date")}
                            </RequiredLabel>
                            <FormControl>
                              <DatePicker
                                value={(field.value as Date | null) || undefined}
                                onChange={(date) => field.onChange(date || null)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={"policy_end_date" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel required>
                              {t("products.auto.fields.policy_end_date")}
                            </RequiredLabel>
                            <FormControl>
                              <DatePicker
                                value={(field.value as Date | null) || undefined}
                                onChange={(date) => field.onChange(date || null)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={"has_estipulante" as AutoPath}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <FormLabel>
                              {t("products.vehicle.fields.has_estipulante")}
                            </FormLabel>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormItem>
                        )}
                      />
                      {form.watch("has_estipulante" as AutoPath) === true && (
                        <FormField
                          control={form.control}
                          name={"insurance_policy_id" as AutoPath}
                          render={({ field }) => (
                            <FormItem>
                              <RequiredLabel required>
                                {t("products.vehicle.fields.stipulant_policy_id")}
                              </RequiredLabel>
                              <FormControl>
                                <Input
                                  value={(field.value as string) || ""}
                                  onChange={field.onChange}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <FormField
                        control={form.control}
                        name={"has_sub_estipulante" as AutoPath}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <FormLabel>
                              {t("products.vehicle.fields.has_sub_estipulante")}
                            </FormLabel>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormItem>
                        )}
                      />
                    </React.Fragment>
                  )}
                  {vehicleEmission === "apolice" &&
                    isCollective === true &&
                    form.watch("has_sub_estipulante" as AutoPath) === true && (
                      <FormField
                        control={form.control}
                        name={"cod_sub" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <RequiredLabel required htmlFor="cod_sub">
                              {t("products.auto.fields.cod_sub")}
                            </RequiredLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="cod_sub"
                                value={(field.value as string) || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  <FormField
                    control={form.control}
                    name={"susep_code" as AutoPath}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="susep_code">
                          {t("products.auto.fields.susep_code")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="susep_code"
                            value={(field.value as string) || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="type_of_vigency"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="type_of_vigency" required>
                          {t("products.auto.fields.type_of_vigency")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-[100%]">
                              <SelectValue
                                placeholder={t(
                                  "products.form.fields.type_of_recurrence.placeholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {AUTO_VIGENCY_VALUES.map((v) => (
                                  <SelectItem key={v} value={v}>
                                    {t(`product.type_of_vigency.${v}`)}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {vehicleEmission === "bilhete" && (
                    <React.Fragment>
                      <FormField
                        control={form.control}
                        name={"policy_start_date" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="policy_start_date">
                              {t("products.auto.fields.policy_start_date")}
                            </FormLabel>
                            <FormControl>
                              <DatePicker
                                value={(field.value as Date | null) || undefined}
                                onChange={(date) => {
                                  field.onChange(date || null);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={"policy_end_date" as AutoPath}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="policy_end_date">
                              {t("products.auto.fields.policy_end_date")}
                            </FormLabel>
                            <FormControl>
                              <DatePicker
                                value={(field.value as Date | null) || undefined}
                                onChange={(date) => {
                                  field.onChange(date || null);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </React.Fragment>
                  )}
                  {hasKmLimit === true && (
                  <FormField
                    control={form.control}
                    name="km_max_permitted"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="km_max_permitted" required>
                          {t("products.auto.fields.km_max_permitted")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="km_max_permitted"
                            type="number"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  )}
                  {hasYearLimit === true && (
                  <FormField
                    control={form.control}
                    name={"max_vehicle_age" as AutoPath}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="max_vehicle_age" required>
                          {t("products.vehicle.fields.max_vehicle_age")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="max_vehicle_age"
                            type="number"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  )}
                  <FormField
                    control={form.control}
                    name="min_manufacturing_year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="min_manufacturing_year">
                          {t("products.auto.fields.min_manufacturing_year")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="min_manufacturing_year"
                            type="number"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.auto.fields.description")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_coverages"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.auto.fields.coverage")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_benefits"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.benefits")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={"excluded_risks" as AutoPath}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.vehicle.fields.excluded_risks")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={(field.value as string) || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="general_conditions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="general_conditions">
                        {t("products.auto.fields.general_conditions")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {hasMultiplePlans === false && (
                  <div className="flex justify-end pt-4">
                    <Button type="submit">
                      {isEditMode
                        ? t("products.button.update")
                        : t("products.button.create")}
                    </Button>
                  </div>
                )}
              </Card>
            </TabsContent>
            {/* Carregamento comercial: etapa removida (planilha Seguro Auto/Moto). */}
            <TabsContent value="price">
              <Card className="p-6">
                <p className="text-sm text-muted-foreground border rounded-md p-3 mb-6">
                  {t("products.auto.quoteNotice")}
                </p>
                {plansFields.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-lg mb-4">
                      {t("products.auto.plansEmptyTitle")}
                    </p>
                    <p className="text-sm">
                      {t("products.auto.plansEmptyHint")}
                    </p>
                  </div>
                )}
                
                <div className="space-y-8">
                  {plansFields.map((plan, planIndex) => (
                    <Card key={plan.id} className="border-2 border-border shadow-md">
                      <div className="bg-muted/50 px-6 py-4 border-b border-border flex items-center justify-between">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                            {planIndex + 1}
                          </span>
                          Plano {planIndex + 1}
                        </h3>
                        {plansFields.length > 1 && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removePlanHandler(planIndex)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remover Plano
                          </Button>
                        )}
                      </div>
                      
                      <div className="p-6 space-y-6">
                        {/* Campos do Plano */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-border">
                          <FormField
                            control={form.control}
                            name={`plans.${planIndex}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel htmlFor={`plan-name-${planIndex}`} required>
                                  Nome do Plano
                                </RequiredLabel>
                                <FormControl>
                                  <Input {...field} id={`plan-name-${planIndex}`} placeholder="Ex: Grupo 1" className="h-11" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`plans.${planIndex}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel htmlFor={`plan-description-${planIndex}`}>
                                  Descrição
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} id={`plan-description-${planIndex}`} placeholder="Ex: Ford, Chevrolet, Toyota" value={field.value || ""} className="h-11" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`plans.${planIndex}.new_coverages` as AutoPath}
                            render={({ field }) => (
                              <FormItem className="md:col-span-2">
                                <FormLabel>
                                  {t("products.vehicle.plan.new_coverages")}
                                </FormLabel>
                                <FormControl>
                                  <RichTextEditor
                                    content={(field.value as string) || ""}
                                    onChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="space-y-6">
                          <PlanAdjustmentsSection
                            form={form}
                            planIndex={planIndex}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}

                  {/* Botão adicionar plano */}
                  <Button type="button" variant="default" size="lg" onClick={addPlan} className="w-full">
                    <Plus className="w-5 h-5 mr-2" />
                    {t("products.auto.addPlan")}
                  </Button>
                </div>
                <div className="flex justify-end mt-8">
                  <Button type="submit">
                    {isEditMode
                      ? t("products.button.update")
                      : t("products.button.create")}
                  </Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    );
  }
};

